import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ScrollText, Search } from "lucide-react";
import { useState } from "react";
import { AUDIT_ACTIONS, type AuditLogRecord } from "@hostdock/types";
import {
  DataTable, Dialog, EmptyState, formatDateTime, humanize, Input, KeyValue, PageHeader, Pagination, Panel, Select, StatusBadge,
} from "@hostdock/ui";
import { api } from "../api";
import { actionLabel, ActorCell } from "../lib/audit-labels";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";

const PAGE_SIZE = 50;
const RANGES: Record<string, number> = { "1h": 3600e3, "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 };

export function AuditPage() {
  return (
    <RequirePermission anyOf={["AUDIT_VIEW"]}>
      <Audit />
    </RequirePermission>
  );
}

function Audit() {
  const { values, page, set } = useListParams(["search", "action", "actorType", "result", "range"] as const);
  const [search, setSearch] = useState(values.search);
  const [selected, setSelected] = useState<AuditLogRecord | null>(null);
  const from = values.range && RANGES[values.range] ? new Date(Date.now() - RANGES[values.range]!).toISOString() : undefined;
  const q = useQuery({
    queryKey: ["audit", values, page],
    queryFn: () => api.audit.list({ search: values.search, action: values.action, actorType: values.actorType, result: values.result, from, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const filtered = Boolean(values.search || values.action || values.actorType || values.result || values.range);

  return (
    <>
      <PageHeader title="Audit Logs" description="Append-only record of sensitive operations. Entries cannot be edited or deleted." />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <form className="relative min-w-52 flex-1" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim() }); }}>
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Actor, target, IP or request ID" className="pl-8" aria-label="Search audit log" />
          </form>
          <Select value={values.action} onChange={(e) => set({ action: e.target.value })} className="w-52" aria-label="Action">
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{humanize(a)}</option>)}
          </Select>
          <Select value={values.actorType} onChange={(e) => set({ actorType: e.target.value })} className="w-36" aria-label="Actor type">
            <option value="">All actors</option>
            <option value="ADMIN">Admins</option>
            <option value="CUSTOMER">Customers</option>
            <option value="SYSTEM">System</option>
          </Select>
          <Select value={values.result} onChange={(e) => set({ result: e.target.value })} className="w-32" aria-label="Result">
            <option value="">Any result</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
            <option value="DENIED">Denied</option>
          </Select>
          <Select value={values.range} onChange={(e) => set({ range: e.target.value })} className="w-32" aria-label="Time range">
            <option value="">All time</option>
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(r) => r.id}
          onRowClick={setSelected}
          empty={<EmptyState icon={ScrollText} title={filtered ? "No entries match these filters" : "No audit entries yet"} />}
          columns={[
            { key: "when", header: "Time", cell: (r) => <span className="whitespace-nowrap text-muted tabular-nums">{formatDateTime(r.occurredAt)}</span> },
            { key: "action", header: "Action", cell: (r) => <span className="font-medium">{actionLabel(r.action)}</span> },
            { key: "actor", header: "Actor", cell: (r) => <ActorCell entry={r} /> },
            { key: "target", header: "Target", cell: (r) => (r.targetType ? <span className="text-muted">{r.targetType}</span> : "—"), hideOnMobile: true },
            { key: "ip", header: "IP", cell: (r) => <span className="font-mono text-[12px]">{r.ip ?? "—"}</span>, hideOnMobile: true },
            { key: "result", header: "Result", cell: (r) => <StatusBadge status={r.result} /> },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)} title={selected ? actionLabel(selected.action) : ""} size="lg">
        {selected && (
          <div className="flex flex-col gap-4">
            <KeyValue
              items={[
                { label: "Time", value: formatDateTime(selected.occurredAt) },
                { label: "Result", value: <StatusBadge status={selected.result} /> },
                { label: "Actor", value: <ActorCell entry={selected} /> },
                { label: "Actor ID", value: <span className="font-mono text-[12px]">{selected.actorId ?? "—"}</span> },
                { label: "Target", value: selected.targetType ? <span className="font-mono text-[12px]">{selected.targetType}:{selected.targetId}</span> : "—" },
                { label: "Customer ID", value: <span className="font-mono text-[12px]">{selected.customerId ?? "—"}</span> },
                { label: "IP address", value: <span className="font-mono text-[12px]">{selected.ip ?? "—"}</span> },
                { label: "User agent", value: <span className="text-[12px] text-muted">{selected.userAgent ?? "—"}</span> },
                { label: "Request ID", value: <span className="font-mono text-[12px]">{selected.correlationId ?? "—"}</span> },
              ]}
            />
            <div>
              <p className="mb-1.5 text-[13px] font-medium">Metadata</p>
              <pre className="max-h-80 overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-[12px]">{JSON.stringify(selected.metadata, null, 2)}</pre>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
